/**
 * When a written text goes to the model.
 *
 * The question this answers: a text is typed, mistakes are in it — at what
 * moment does the model read it and turn them into exercises?
 *
 * It used to happen while writing. Every correction Elmar accepted sent its
 * sentence off on the next sync, one call per sentence, mid-sentence texts
 * included. Three things were wrong with that: it cost a call for every
 * accepted word, the model saw fragments instead of the text, and there was
 * no moment at which anything could still be taken back.
 *
 * Now it happens on the START SCREEN. Opening the app, coming back from the
 * paper, before tapping „Wiederholen" — every path goes through there, and
 * it is the one place that is not in the middle of something. The text is
 * read as a whole, once per version of it.
 *
 * Not on leaving the paper: whoever is finished writing may simply shut the
 * computer, and a screen that is being closed is no place to start a
 * network call that costs money.
 *
 * What makes a text „unread" is its `rev` — the counter the editor raises on
 * every save. The rev that was last reviewed is remembered per text; a text
 * whose rev has moved since is due again, which is also what happens when he
 * writes on further in a text from last week.
 */

import * as store from '../editor/store.js'
import type { Doc } from '../types.js'

/** Where the reviewed revisions are kept: text id → the rev that was read. */
const KEY = 'reviewedRevs'

/**
 * How long a text has to be before it is worth a call.
 *
 * A started title is not a text. Measured on the kind of sentence a
 * twelve-year-old writes — „Gestern waren wir im zoo und haben die affen
 * gesehen" — one of them runs to about ten words, and the shortest to six.
 * Fifteen was tried first and would have thrown away one-sentence texts,
 * which are exactly the ones worth reading when he has just started.
 */
export const MIN_WORDS = 10

type Reviewed = Record<string, number>

const read = async (): Promise<Reviewed> => {
  const v = await store.getPref(KEY).catch(() => undefined)
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Reviewed) : {}
}

/** Counts the words of a text — the same shape the writing time counts. */
export function words(text: string): number {
  return (text.match(/[\p{L}\p{N}]+(?:[’'-][\p{L}\p{N}]+)*/gu) ?? []).length
}

/**
 * How far back a text is still worth reading.
 *
 * Whoever comes back after two weeks away wants to go on practising, not to
 * sit through a queue of everything ever written. Anything older than this
 * is quietly counted as read: it has served its purpose, and the mistakes
 * worth practising have long been collected elsewhere.
 *
 * Fourteen days covers an ordinary holiday. It matters most on the first run
 * after this was built, where every text ever written counts as unread.
 */
const MAX_AGE = 14 * 24 * 60 * 60 * 1000

/**
 * The texts waiting to be read, NEWEST change first.
 *
 * Newest first, not oldest: the text he has just written is the one he will
 * practise from, and the one he is waiting for. An old one that surfaces
 * behind it can wait for the next time the start screen opens.
 *
 * Deleted texts are left out, and so are the ones too short to be worth it.
 * A text that has not changed since it was read is not here either — that is
 * what keeps this from costing anything on a start screen opened twice in a
 * row.
 */
export async function dueForReview(now = Date.now()): Promise<Doc[]> {
  const [docs, reviewed] = await Promise.all([
    store.allDocs().catch(() => [] as Doc[]),
    read(),
  ])
  return docs
    .filter((d) => !d.deleted)
    .filter((d) => words(d.body) >= MIN_WORDS)
    .filter((d) => reviewed[d.id] !== d.rev)
    .filter((d) => now - d.changed <= MAX_AGE)
    .sort((a, b) => b.changed - a.changed)
}

/**
 * Counts everything present as read, without reading any of it.
 *
 * For the first run after this was built: without it every text ever written
 * would be read one after another, at a call each. What is in the app now is
 * taken as known; from here on only what changes is read.
 */
export async function markAllReviewed(): Promise<void> {
  const docs = await store.allDocs().catch(() => [] as Doc[])
  const r = await read()
  for (const d of docs) {
    if (!d.deleted) r[d.id] = d.rev
  }
  await store.setPref(KEY, r).catch(() => {})
}

/**
 * Notes that this version of the text has been read.
 *
 * Stored per text and per rev, so writing on further makes it due again
 * while opening the start screen ten times does not.
 */
export async function markReviewed(id: string, rev: number): Promise<void> {
  const r = await read()
  r[id] = rev
  await store.setPref(KEY, r).catch(() => {})
}

/**
 * Forgets texts that are no longer there.
 *
 * Without this the record would grow for ever — one entry per text ever
 * written, long after the text itself was thrown away.
 */
export async function forgetGone(): Promise<void> {
  const [docs, reviewed] = await Promise.all([
    store.allDocs().catch(() => [] as Doc[]),
    read(),
  ])
  const alive = new Set(docs.filter((d) => !d.deleted).map((d) => d.id))
  const keep: Reviewed = {}
  for (const [id, rev] of Object.entries(reviewed)) {
    if (alive.has(id) && typeof rev === 'number') keep[id] = rev
  }
  if (Object.keys(keep).length !== Object.keys(reviewed).length) {
    await store.setPref(KEY, keep).catch(() => {})
  }
}
